// ─── Supabase config — single source of truth ────────────────────────────────
// Values come from environment variables. No fallbacks — the app throws
// immediately on startup if either is missing. This prevents silent operation
// against a wrong or missing Supabase project.
// Dev: set in .env.local (gitignored). Prod: Vercel dashboard → Env Vars.
function requireEnv(name) {
  const val = import.meta.env[name];
  if (!val) {
    throw new Error(
      `[Pazque] Missing required environment variable: ${name}\n` +
      `Set it in .env.local (dev) or Vercel dashboard (production).`
    );
  }
  return val;
}

export const SB_URL = requireEnv('VITE_SUPABASE_URL');
export const SKEY   = requireEnv('VITE_SUPABASE_ANON_KEY');

// ─── Auth headers ─────────────────────────────────────────────────────────────
// Reads the live session token from localStorage on every call so headers
// stay fresh after a JWT refresh.
// CRITICAL: apikey must always be the anon key (SKEY).
// The user JWT goes only in Authorization — never in apikey.
export const getAuthHeaders = (extra = {}) => {
  // apikey is always the public anon key — identifies the Supabase project.
  // Authorization must be the user's JWT. If there is no valid session,
  // we throw immediately so the caller fails explicitly rather than making
  // a request as the 'anon' role, which would silently bypass authenticated RLS.
  const session = JSON.parse(localStorage.getItem('aryes-session') || 'null');
  const token = session?.access_token;
  if (!token) {
    throw new Error('[auth] No active session — request aborted');
  }
  return {
    'apikey': SKEY,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...extra,
  };
};


// ─── getSession — single source of truth para la sesión ──────────────────────
export const getSession = () => {
  try { return JSON.parse(localStorage.getItem('aryes-session') || 'null'); }
  catch { return null; }
};

// ─── getOrgId — lee org desde sesión (default: 'aryes') ──────────────────────
// ─── getOrgId — reads org from session (defaults to org from session) ─────────────────
export const getOrgId = () => {
  try {
    const session = JSON.parse(localStorage.getItem('aryes-session') || 'null');
    return session?.orgId || '__no_org__';
  } catch { return '__no_org__'; }
};

// ─── LS — localStorage cache ──────────────────────────────────────────────────
// Supabase is the source of truth. localStorage is a read cache populated by
// AppContext on login and updated optimistically on every mutation.
export const LS = {
  get(key, def) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null || raw === undefined) return def;
      try { return JSON.parse(raw); } catch { return raw; }
    } catch { return def; }
  },

  set(key, value) {
    try {
      const str = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, str);
    } catch { /* never block */ }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch { /* never block */ }
  },
};

// ─── refreshSession — ÚNICA fuente de refresco del JWT ────────────────────────
// CRÍTICO: Supabase rota el refresh_token en cada uso (single-use). Si hubiera
// dos lugares refrescando por separado, el segundo usaría un refresh_token ya
// revocado → 400 → deslogueo accidental. Por eso TODO el refresco pasa por acá,
// con deduplicación (_refreshInFlight) para que llamadas concurrentes compartan
// el mismo request. Actualiza localStorage y avisa a la app por eventos.
let _refreshInFlight = null;
export async function refreshSession() {
  if (_refreshInFlight) return _refreshInFlight;
  const s = getSession();
  if (!s?.refresh_token) return null;
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${SB_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: SKEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: s.refresh_token }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.access_token) {
        const next = {
          ...getSession(),
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
        };
        LS.set('aryes-session', next);
        try { window.dispatchEvent(new CustomEvent('aryes-session-refreshed', { detail: next })); } catch { /* SSR */ }
        return next;
      }
      // refresh_token inválido/revocado → sesión muerta, hay que re-loguear.
      if (res.status === 400 || res.status === 401) {
        try { window.dispatchEvent(new CustomEvent('aryes-session-dead')); } catch { /* SSR */ }
      }
      // Otros errores (5xx/red): no tocamos la sesión; se reintenta luego.
      return null;
    } catch {
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

// ─── fetchWithRefresh — auto-recuperación ante token vencido ──────────────────
// Hace el request; si vuelve 401 (token expiró justo antes del guardado),
// refresca el JWT una vez y reintenta. doFetch DEBE reconstruir sus headers en
// cada llamada (vía getAuthHeaders, que lee el token fresco de localStorage),
// así el reintento sale con el token nuevo. Esto evita el banner "Cambios
// pendientes" por expiración de sesión sin esperar al timer proactivo.
async function fetchWithRefresh(doFetch) {
  let r = await doFetch();
  if (r.status === 401) {
    const ns = await refreshSession();
    if (ns) r = await doFetch();
  }
  return r;
}

// ─── db — Supabase REST client ────────────────────────────────────────────────
export const db = {
  async get(table, query = '') {
    // Demo mode guard — skip Supabase when no session exists (demo mode)
    const _s = getSession();
    if (!_s?.access_token) return null;
    // Support both contracts:
    //   db.get('app_config', 'key=eq.x')        → /rest/v1/app_config?key=eq.x
    //   db.get('app_config?key=eq.x&org_id=eq.y') → /rest/v1/app_config?key=eq.x&org_id=eq.y
    // Without this guard, the second contract appended a stray '?' that broke Supabase filters.
    const url = table.includes('?')
      ? `${SB_URL}/rest/v1/${table}${query ? '&' + query : ''}`
      : `${SB_URL}/rest/v1/${table}?${query}`;
    let r;
    try {
      r = await fetchWithRefresh(() => fetch(url, {
        headers: getAuthHeaders({ 'Prefer': 'return=representation' }),
      }));
    } catch (networkErr) {
      // Network failure — return null so callers preserve existing state
      console.warn('[db.get] network error:', table, networkErr?.message);
      return null;
    }
    if (r.ok) return r.json();
    // Auth failure (401/403) or server error (5xx) — return null, NOT []
    // Returning [] would overwrite valid local state with empty data
    console.warn('[db.get] non-ok response:', table, r.status);
    return null;
  },

  async upsert(table, data, conflictCol = '') {
    // Demo mode guard — skip Supabase when no session (demo mode)
    const _s = getSession();
    if (!_s?.access_token) return [data];
    const payload = data.org_id !== undefined ? data : { ...data, org_id: getOrgId() };
    const url = `${SB_URL}/rest/v1/${table}${conflictCol ? `?on_conflict=${conflictCol}` : ''}`;
    const r = await fetchWithRefresh(() => fetch(url, {
      method: 'POST',
      headers: getAuthHeaders({ 'Prefer': 'resolution=merge-duplicates,return=representation' }),
      body: JSON.stringify(payload),
    }));
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      console.warn('[db] upsert failed:', table, e?.message || r.status);
      // Dispatch global error event so UI can show feedback
      window.dispatchEvent(new CustomEvent('aryes-sync-error', { detail: { table, error: e?.message || 'Error al guardar' } }));
    }
    return r.ok ? r.json() : null;
  },

  async patch(table, data, match) {
    // Demo mode guard — skip when no session
    if (!getSession()?.access_token) return [data];
    const query = typeof match === 'string'
      ? match
      : Object.entries(match).map(([k, v]) => `${k}=eq.${v}`).join('&');
    const orgFilter = query.includes('org_id') ? '' : `&org_id=eq.${getOrgId()}`;
    const r = await fetchWithRefresh(() => fetch(`${SB_URL}/rest/v1/${table}?${query}${orgFilter}`, {
      method: 'PATCH',
      headers: getAuthHeaders({ 'Prefer': 'return=representation' }),
      body: JSON.stringify(data),
    }));
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      console.warn('[db] patch failed:', table, e?.message || r.status);
      window.dispatchEvent(new CustomEvent('aryes-sync-error', { detail: { table, error: e?.message || 'Error al guardar' } }));
    }
    return r.ok ? r.json() : null;
  },

  async del(table, match) {
    // Demo mode guard — skip when no session
    if (!getSession()?.access_token) return;
    const query = Object.entries(match).map(([k, v]) => `${k}=eq.${v}`).join('&');
    const orgFilter = `&org_id=eq.${getOrgId()}`;
    await fetchWithRefresh(() => fetch(`${SB_URL}/rest/v1/${table}?${query}${orgFilter}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }));
  },

  async insert(table, row) {
    // Auto-inject org_id if not already present — enables multi-tenancy transparently
    const payload = row.org_id !== undefined ? row : { ...row, org_id: getOrgId() };
    const r = await fetchWithRefresh(() => fetch(`${SB_URL}/rest/v1/${table}`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Prefer': 'return=representation,resolution=ignore-duplicates' }),
      body: JSON.stringify(payload),
    }));
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      console.warn('[db] insert failed:', table, e?.message || r.status);
    }
    return r.ok ? r.json() : null;
  },

  async insertMany(table, rows) {
    if (!rows.length) return;
    const orgId = getOrgId();
    const payload = rows.map(r => r.org_id !== undefined ? r : { ...r, org_id: orgId });
    const r = await fetch(`${SB_URL}/rest/v1/${table}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Prefer': 'return=minimal' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const e = await r.text();
      throw new Error(`db.insertMany ${table}: ${e}`);
    }
  },

  // Optimistic concurrency: only patches if lockField still equals lockValue.
  // Prevents stale-read stock overwrites under concurrent writes.
  // On conflict (0 rows updated): re-fetches, recalculates delta, retries.
  async patchWithLock(table, data, filter, lockField, lockValue, maxRetries = 3) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const orgFilter = filter.includes('org_id') ? '' : `&org_id=eq.${getOrgId()}`;
      const lockFilter = `${filter}&${lockField}=eq.${lockValue}${orgFilter}`;
      const r = await fetchWithRefresh(() => fetch(`${SB_URL}/rest/v1/${table}?${lockFilter}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Prefer': 'return=representation,count=exact' },
        body: JSON.stringify(data),
      }));
      if (!r.ok) {
        const e = await r.text();
        throw new Error(`patchWithLock ${table}: ${e}`);
      }
      const rows = await r.json();
      if (rows.length > 0) return rows[0];

      if (attempt < maxRetries - 1) {
        const fresh = await this.get(`${table}?${filter}`);
        if (!fresh?.[0]) throw new Error('patchWithLock: row not found');
        const delta = data.stock !== undefined ? data.stock - lockValue : 0;
        lockValue = fresh[0][lockField];
        if (data.stock !== undefined) data = { ...data, stock: lockValue + delta };
      } else {
        throw new Error(`patchWithLock: conflict after ${maxRetries} retries`);
      }
    }
  },
};


// ─── fmt — formateo de números centralizado ───────────────────────────────────
// Single source of truth para todos los formatos de números en la UI.
// Usar siempre estas funciones — nunca toLocaleString/toFixed directo.

// Símbolo de moneda por-org. Arranca en '$' (UYU, comportamiento histórico) y lo
// setea el portal una sola vez al cargar el catálogo (fmt.setCurrency), así los
// ~40 llamados a fmt.currency(n) sin argumento muestran la moneda de la org
// (Perú S/, Paraguay ₲, etc.) sin tocar cada call site. Si alguien pasa un código
// explícito (fmt.currency(n, 'USD')) ese override manda y no usa este default.
let _orgCurrencySym = '$';
function _symFromCode(currency) {
  return currency === 'UYU' ? '$'
    : currency === 'USD' ? 'US$'
    : currency === 'EUR' ? '€'
    : currency;
}

export const fmt = {
  // Define el símbolo de moneda por defecto para toda la sesión del portal.
  setCurrency: (sym) => { if (sym && typeof sym === 'string') _orgCurrencySym = sym; },

  // Moneda con símbolo — $1.234 / US$1.234. Sin 2º arg usa el símbolo de la org.
  currency: (n, currency) => {
    const num = Number(n || 0);
    const sym = currency == null ? _orgCurrencySym : _symFromCode(currency);
    return `${sym} ${num.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },

  // Precio UNITARIO — hasta 3 decimales ($ 12,50 · $ 12,345). Para precio por
  // kg/unidad donde la precisión importa (wholesale). Los TOTALES siguen usando
  // currency() (2 decimales, estándar de moneda). Sin 2º arg usa el símbolo org.
  price: (n, currency) => {
    const num = Number(n || 0);
    const sym = currency == null ? _orgCurrencySym : _symFromCode(currency);
    return `${sym} ${num.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
  },

  // Moneda compacta — $1.2k / US$34k
  currencyCompact: (n, currency) => {
    const num = Number(n || 0);
    const sym = currency == null ? _orgCurrencySym : _symFromCode(currency);
    if (num >= 1_000_000) return `${sym} ${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000)     return `${sym} ${(num / 1_000).toFixed(1)}k`;
    return `${sym} ${num.toFixed(0)}`;
  },

  // Porcentaje — 12.3%
  percent: (n, decimals = 1) => `${Number(n || 0).toFixed(decimals)}%`,

  // Cantidad con unidad — 3.5 kg / 120 uds
  qty: (n, unit = '', decimals = 1) => {
    const num = Number(n || 0);
    const str = Number.isInteger(num) ? num.toString() : num.toFixed(decimals);
    return unit ? `${str} ${unit}` : str;
  },

  // Número entero con separador de miles — 1.234
  int: (n) => Math.round(Number(n || 0)).toLocaleString('es'),

  // Decimal con n lugares — 1.23
  decimal: (n, places = 2) => Number(n || 0).toFixed(places),
};

// ─── Alert severity config ────────────────────────────────────────────────────
export const ALERT_CFG = {
  order_now:  { label: 'Pedir YA',     dot: '#dc2626', bg: '#fef2f2', bd: '#fecaca', txt: '#dc2626', pri: 3 },
  order_soon: { label: 'Pedir pronto', dot: '#d97706', bg: '#fffbeb', bd: '#fde68a', txt: '#d97706', pri: 2 },
  watch:      { label: 'Vigilar',      dot: '#2563eb', bg: '#eff6ff', bd: '#bfdbfe', txt: '#2563eb', pri: 1 },
  ok:         { label: 'Normal',       dot: '#16a34a', bg: '#f0fdf4', bd: '#bbf7d0', txt: '#16a34a', pri: 0 },
};

export const tfCols = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981'];

// ─── sanitizeText ─────────────────────────────────────────────────────────────
export const sanitizeText = (str, maxLen = 200) => {
  if (!str) return '';
  return String(str).trim().replace(/\s+/g, ' ').slice(0, maxLen);
};
